import { ReactNode, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
    Finding,
    HealthSeverity,
    WORKER_LABEL,
    WorkerFunction,
    WorkerStatus,
    isWorse,
} from '../../api/health';
import { formatAge, formatDuration } from '../../api/queueMonitor';

export type BoardTone = 'good' | 'warn' | 'bad' | 'neutral';

/** The worst severity among some findings, or null when there are none. */
export const worstOf = (findings: Finding[]): HealthSeverity | null =>
    findings.reduce<HealthSeverity | null>(
        (carried, finding) =>
            carried === null || isWorse(finding.severity, carried)
                ? finding.severity
                : carried,
        null
    );

/** A severity as a colour, with no finding at all as calm. */
export const toneOf = (severity: HealthSeverity | null): BoardTone =>
    severity === 'Critical'
        ? 'bad'
        : severity === 'Warning'
        ? 'warn'
        : 'neutral';

export interface IAppFrameProps {
    appKey: string;
    /** The app as the health call knows it; null while that call is still in flight. */
    app: WorkerStatus | null;
    /** The findings about this app as a whole, not about one of its queues. */
    findings: Finding[];
    /** Whether this app's telemetry panel is open. */
    open: boolean;
    onOpen: (appKey: string) => void;
    children: ReactNode;
}

/**
 * One function app, drawn as a frame around the functions it hosts.
 *
 * The app is where the state lives - running, silent, out of sight - and where the ways into
 * Azure lead, so both are said once, in the head of the frame. The functions inside are only
 * names with a trigger. On the old board every function carried the whole app with it, which
 * is how `cbrcv` ended up on screen three times and one finding about one queue on all three.
 */
export const AppFrame = ({
    appKey,
    app,
    findings,
    open,
    onOpen,
    children,
}: IAppFrameProps) => {
    const [expanded, setExpanded] = useState<boolean>(false);

    const verdict = app ? WORKER_LABEL[app.verdict] : null;
    const worst = worstOf(findings);

    /*
        A finding outranks the heartbeat, because a finding is a conclusion drawn from the
        heartbeat plus everything else. Without one the heartbeat decides, and "no signal"
        stays grey rather than turning green: an app that leaves no trace has not been shown
        to be healthy.
    */
    const tone: BoardTone =
        worst === 'Critical' || worst === 'Warning'
            ? toneOf(worst)
            : verdict?.tone === 'bad'
            ? 'bad'
            : verdict?.tone === 'good'
            ? 'good'
            : 'neutral';

    const seen = app?.lastSeenAt
        ? `${formatAge(app.lastSeenAt)} geleden`
        : null;

    const state = !app
        ? 'laden'
        : app.verdict === 'Silent' && app.quietForSeconds
        ? `stil sinds ${formatDuration(app.quietForSeconds)}`
        : app.verdict === 'Alive' && seen
        ? `leefde ${seen}`
        : verdict?.label;

    const title = [
        app ? `${app.displayName} (${app.links?.appName ?? appKey})` : appKey,
        app?.evidence,
        findings.length > 0
            ? findings.map((finding) => `• ${finding.title}`).join('\n')
            : null,
        'Klik om te zien wat deze app heeft gedaan.',
    ]
        .filter(Boolean)
        .join('\n\n');

    return (
        <div className={`app-frame app-frame--${tone}`}>
            <div className="app-frame__head">
                <button
                    type="button"
                    className={`app-frame__name${
                        open ? ' app-frame__name--open' : ''
                    }`}
                    onClick={() => onOpen(appKey)}
                    aria-pressed={open}
                    title={title}
                >
                    <FontAwesomeIcon icon="bolt" className="app-frame__icon" />
                    <code className="app-frame__key">{appKey}</code>
                    {app && (
                        <span className="app-frame__display">
                            {app.displayName}
                        </span>
                    )}
                </button>

                <span className={`app-frame__state app-frame__state--${tone}`}>
                    {state}
                </span>

                {app && <AppLinks app={app} />}

                <button
                    type="button"
                    className="app-frame__more"
                    onClick={() => setExpanded((value) => !value)}
                    aria-expanded={expanded}
                    title="Wat er verder in deze app zit"
                >
                    <FontAwesomeIcon
                        icon={expanded ? 'angle-up' : 'angle-down'}
                    />
                </button>
            </div>

            {findings.length > 0 && (
                <p className="app-frame__finding" title={title}>
                    {findings.length === 1
                        ? findings[0].title
                        : `${findings.length} bevindingen`}
                </p>
            )}

            {expanded && app && <AppDetail app={app} />}

            <div className="app-frame__body">{children}</div>
        </div>
    );
};

/**
 * The ways out of this dashboard and into Azure, as small icon links in the head of the
 * frame.
 *
 * Only the links that can actually be built are drawn. The first is the app itself, in the
 * portal; the site URL survives only as the fallback for an environment whose portal link
 * cannot be built.
 */
const AppLinks = ({ app }: { app: WorkerStatus }) => {
    const links = app.links;

    if (!links) {
        return null;
    }

    const appUrl = links.portal ?? links.site;
    const appLabel = links.appName ?? app.key;

    return (
        <span className="app-frame__links">
            {appUrl && (
                <a
                    className="app-frame__link"
                    href={appUrl}
                    target="_blank"
                    rel="noreferrer"
                    title={
                        links.portal
                            ? `Open de function app ${appLabel} in de Azure portal`
                            : `Open ${appLabel}. Voor deze omgeving is geen abonnement en resourcegroep ingesteld, dus een link naar de portal valt niet te maken; dit is het publieke adres van de app.`
                    }
                >
                    <FontAwesomeIcon icon="arrow-up-right-from-square" />
                    <span className="visually-hidden">Open in Azure</span>
                </a>
            )}

            {links.portalLogStream && (
                <a
                    className="app-frame__link"
                    href={links.portalLogStream}
                    target="_blank"
                    rel="noreferrer"
                    title="De live log stream van deze app. Wat hier langskomt is niet gefilterd door de logdrempel uit host.json, dus dit is de snelste manier om te zien of er iets gebeurt."
                >
                    stream
                </a>
            )}

            {links.logsHost && (
                <a
                    className="app-frame__link"
                    href={links.logsHost}
                    target="_blank"
                    rel="noreferrer"
                    title={`Of de host van deze app is opgestart. Dit is het geval dat de queue niet kan laten zien: een host die tijdens het starten sneuvelt deelt nooit een bericht uit.\n\n${links.queryHost}`}
                >
                    host
                </a>
            )}
        </span>
    );
};

/** What else lives in this app, folded away until somebody asks. */
const AppDetail = ({ app }: { app: WorkerStatus }) => {
    const functions = app.functions ?? [];

    return (
        <div className="app-frame__detail">
            <p className="app-frame__detail-line">
                {app.displayName}
                {app.links?.appName ? ' · ' : ''}
                {app.links?.appName && <code>{app.links.appName}</code>}
                {app.links?.appNameIsDerived && (
                    <span
                        className="app-frame__derived"
                        title="Deze naam is afgeleid uit de naam van het storage account, niet ingesteld. De naamconventie klopt overal waar we hebben gekeken, maar het blijft een conventie."
                    >
                        afgeleid
                    </span>
                )}
            </p>

            <p className="app-frame__detail-line app-frame__detail-line--muted">
                <code>{app.project}</code>
            </p>

            {functions.length > 0 && (
                <ul className="app-frame__functions">
                    {functions.map((fn) => (
                        <li key={fn.name} className="app-frame__function-row">
                            <FontAwesomeIcon
                                icon={TRIGGER_ICON[fn.trigger]}
                                className="app-frame__trigger"
                                title={TRIGGER_LABEL[fn.trigger]}
                            />

                            {fn.logsUrl ? (
                                <a
                                    href={fn.logsUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    title={`Application Insights, gefilterd op ${fn.name}`}
                                >
                                    <code>{fn.name}</code>
                                </a>
                            ) : (
                                <code>{fn.name}</code>
                            )}

                            <span className="app-frame__function-meta">
                                {describeTrigger(fn)}
                            </span>
                        </li>
                    ))}
                </ul>
            )}

            <p className="app-frame__evidence">{app.evidence}</p>
        </div>
    );
};

export const TRIGGER_ICON: Record<WorkerFunction['trigger'], IconProp> = {
    Queue: 'list-ul',
    Http: 'globe',
    Timer: 'clock',
};

export const TRIGGER_LABEL: Record<WorkerFunction['trigger'], string> = {
    Queue: 'queue-trigger',
    Http: 'http-trigger',
    Timer: 'timer',
};

/** What sets this function off, in as few words as fit on the row. */
const describeTrigger = (fn: WorkerFunction): string => {
    if (fn.trigger === 'Queue') {
        return fn.queue ? `leest ${fn.queue}` : 'queue-trigger';
    }

    if (fn.trigger === 'Http') {
        return fn.writesQueue
            ? `http, schrijft naar ${fn.writesQueue}`
            : 'http';
    }

    const schedule = fn.intervalSeconds
        ? `elke ${formatDuration(fn.intervalSeconds)}`
        : 'timer, schema in een app-setting';

    return fn.writesQueue
        ? `${schedule}, schrijft naar ${fn.writesQueue}`
        : schedule;
};
